import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLoginPageImages } from '../../hooks/useContent';
import Header from '../../components/Layout/Header';
import Footer from '../../components/Layout/Footer';
import './Auth.css';

const DEFAULT_LOGIN_GRID_IMAGE =
  'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=300';

const Login = () => {
  const [mobileNumber, setMobileNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone'); // 'phone' | 'otp' | 'register'
  const [registerName, setRegisterName] = useState({ firstName: '', lastName: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { sendOtp, loginWithOtp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { images: loginImages, loading: imagesLoading } = useLoginPageImages();
  const from = location.state?.from;

  const postLoginRedirect = () => {
    if (from && from !== '/login') {
      navigate(from, { replace: true });
      return;
    }
    const hasRedirectedBefore = sessionStorage.getItem('postLoginRedirectToProfile');
    if (!hasRedirectedBefore) {
      sessionStorage.setItem('postLoginRedirectToProfile', '1');
      navigate('/profile', { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  };

  // Process images from API — 9 tiles (positions 1–9); center is a photo like the rest.
  const imageGrid = (() => {
    if (imagesLoading || loginImages.length === 0) {
      return Array.from({ length: 9 }, (_, i) => ({
        id: `placeholder-${i + 1}`,
        url: DEFAULT_LOGIN_GRID_IMAGE,
        position: i + 1
      }));
    }

    const imageMap = {};
    loginImages.forEach((img) => {
      imageMap[img.position] = img;
    });

    return Array.from({ length: 9 }, (_, i) => {
      const position = i + 1;
      const img = imageMap[position];
      if (img) {
        return {
          id: img.id,
          url: (img.imageUrl && String(img.imageUrl).trim()) || DEFAULT_LOGIN_GRID_IMAGE,
          position: img.position
        };
      }
      return {
        id: `placeholder-${position}`,
        url: DEFAULT_LOGIN_GRID_IMAGE,
        position
      };
    });
  })();

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only numbers
    if (value.length <= 10) {
      setMobileNumber(value);
    }
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    if (mobileNumber.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }
    setLoading(true);
    const result = await sendOtp(mobileNumber);
    setLoading(false);
    if (result.success) {
      setStep('otp');
      setOtp('');
    } else {
      setError(result.message || 'Failed to send OTP');
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    if (!otp.trim()) {
      setError('Please enter the OTP');
      return;
    }
    setLoading(true);
    const result = await loginWithOtp(mobileNumber, otp.trim());
    setLoading(false);
    if (result.success) {
      if (result.needsRegister) {
        setStep('register');
        setRegisterName({ firstName: '', lastName: '' });
      } else {
        setTimeout(() => postLoginRedirect(), 0);
      }
    } else {
      setError(result.message || 'Invalid OTP');
    }
  };

  const handleCompleteRegister = async (e) => {
    e.preventDefault();
    setError('');
    const { firstName, lastName } = registerName;
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter first and last name');
      return;
    }
    setLoading(true);
    const result = await loginWithOtp(mobileNumber, otp, { firstName: firstName.trim(), lastName: lastName.trim() });
    setLoading(false);
    if (result.success) {
      setTimeout(() => postLoginRedirect(), 0);
    } else {
      setError(result.message || 'Registration failed');
    }
  };

  return (
    <div className="login-page-wrapper">
      <Header />
      <div className="login-page">
        <div className="login-container">
          {/* Left Section - Login Form */}
          <div className="login-form-card">
            <div className="login-form-header">
              <h1 className="login-title">Welcome To Invincible</h1>
              <p className="login-subtitle">Log In Or Sign Up To Continue</p>
            </div>
            
            {error && <div className="error-message">{error}</div>}

            {step === 'phone' && (
              <form onSubmit={handleSendOTP} className="login-form">
                <p className="login-hint">New user? Same flow — enter your phone and we&apos;ll create your account after OTP.</p>
                <div className="form-group">
                  <label htmlFor="mobile">Mobile Number</label>
                  <div className="mobile-input-group">
                    <span className="mobile-prefix">+91</span>
                    <input
                      type="tel"
                      id="mobile"
                      name="mobile"
                      value={mobileNumber}
                      onChange={handleMobileChange}
                      placeholder="Enter Mobile Number"
                      className="mobile-input"
                      required
                      maxLength="10"
                    />
                  </div>
                </div>
                <button type="submit" disabled={loading || mobileNumber.length !== 10} className="submit-btn-primary">
                  {loading ? 'Sending...' : 'Send OTP'}
                </button>
                <button
                  type="button"
                  className="submit-btn-secondary"
                  disabled
                  aria-disabled="true"
                  title="Coming soon"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                  <span className="login-volunteer-btn-label">
                    <span className="login-volunteer-btn-title">Login As Volunteer</span>
                    <span className="login-volunteer-btn-badge">Coming soon</span>
                  </span>
                </button>
                <p className="terms-text">
                  By Continuing, You Agree To Our <Link to="/terms" className="terms-link">Terms of Service</Link> and <Link to="/privacy" className="terms-link">Privacy Policy</Link>.
                </p>
              </form>
            )}

            {step === 'otp' && (
              <form onSubmit={handleVerifyOTP} className="login-form">
                <p className="otp-sent-msg">OTP sent to +91 {mobileNumber}. <button type="button" className="link-btn" onClick={() => { setStep('phone'); setOtp(''); setError(''); }}>Change number</button></p>
                <div className="form-group">
                  <label htmlFor="otp">Enter OTP</label>
                  <input
                    type="text"
                    id="otp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="6-digit OTP"
                    className="mobile-input"
                    maxLength="6"
                  />
                </div>
                <button type="submit" disabled={loading || otp.length < 4} className="submit-btn-primary">
                  {loading ? 'Verifying...' : 'Verify & Continue'}
                </button>
                <p className="terms-text">
                  By Continuing, You Agree To Our <Link to="/terms" className="terms-link">Terms of Service</Link> and <Link to="/privacy" className="terms-link">Privacy Policy</Link>.
                </p>
              </form>
            )}

            {step === 'register' && (
              <form onSubmit={handleCompleteRegister} className="login-form">
                <p className="otp-sent-msg">Complete registration for +91 {mobileNumber}</p>
                <div className="form-row" style={{ display: 'flex', gap: '12px' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="firstName">First Name</label>
                    <input
                      type="text"
                      id="firstName"
                      value={registerName.firstName}
                      onChange={(e) => setRegisterName((p) => ({ ...p, firstName: e.target.value }))}
                      placeholder="First name"
                      className="mobile-input"
                      required
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="lastName">Last Name</label>
                    <input
                      type="text"
                      id="lastName"
                      value={registerName.lastName}
                      onChange={(e) => setRegisterName((p) => ({ ...p, lastName: e.target.value }))}
                      placeholder="Last name"
                      className="mobile-input"
                      required
                    />
                  </div>
                </div>
                <button type="submit" disabled={loading} className="submit-btn-primary">
                  {loading ? 'Creating account...' : 'Complete Sign Up'}
                </button>
                <p className="terms-text">
                  By Continuing, You Agree To Our <Link to="/terms" className="terms-link">Terms of Service</Link> and <Link to="/privacy" className="terms-link">Privacy Policy</Link>.
                </p>
              </form>
            )}

          </div>

          {/* Right Section - Image Grid */}
          <div className="login-image-grid-card">
            <div className="image-grid">
              {imageGrid.map((item, index) => (
                <div key={item.id || index} className="grid-image-item">
                  {item.url ? <img src={item.url} alt={`Adventure ${index + 1}`} loading="lazy" /> : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Login;
